import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import caseyLogo from "@/assets/casey-logo.png";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);

  const closeSheet = () => setIsOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center gap-2 text-xl sm:text-2xl font-medium text-foreground hover:text-primary transition-colors">
              <img src={caseyLogo} alt="tellcasey logo" className="h-7 w-7 sm:h-8 sm:w-8" />
              tellcasey
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <a href="/#product" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Product
            </a>
            <a href="/#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              How it works
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                Solutions
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem asChild>
                  <a href="/#use-cases">Customer Proof</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/solutions/market-research-ai">Market Research</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/solutions/hospitality-ai">Hospitality</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/resources" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Resources
            </Link>
            <a href="/#faq" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              FAQ
            </a>
            <a href="/#contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Contact
            </a>
          </nav>

          {/* CTAs and Mobile Menu */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex" asChild>
              <a href="https://app.tellcasey.com/auth/login" target="_blank" rel="noopener noreferrer">Login</a>
            </Button>
            <Button variant="outline" size="sm" className="hidden sm:inline-flex" asChild>
              <Link to="/early-access">Book a Demo</Link>
            </Button>
            <Button size="sm" className="hidden sm:inline-flex" asChild>
              <Link to="/early-access">Get Started</Link>
            </Button>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <SheetHeader>
                  <SheetTitle className="text-left text-xl font-bold">tellcasey</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 mt-8">
                  <a 
                    href="/#product" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Product
                  </a>
                  <a 
                    href="/#how-it-works" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    How it works
                  </a>
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Solutions
                  </div>
                  <a 
                    href="/#use-cases" 
                    onClick={closeSheet}
                    className="px-4 py-3 pl-6 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Customer Proof
                  </a>
                  <Link 
                    to="/solutions/market-research-ai" 
                    onClick={closeSheet}
                    className="px-4 py-3 pl-6 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Market Research
                  </Link>
                  <Link 
                    to="/solutions/hospitality-ai" 
                    onClick={closeSheet}
                    className="px-4 py-3 pl-6 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Hospitality
                  </Link>
                  
                  <Link 
                    to="/resources" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Resources
                  </Link>

                  <a 
                    href="/#faq" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    FAQ
                  </a>
                  <a 
                    href="/#contact" 
                    onClick={closeSheet}
                    className="px-4 py-3 text-base font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    Contact
                  </a>

                  {/* Mobile CTAs */}
                  <div className="flex flex-col gap-3 mt-6 px-4">
                    <Button variant="ghost" asChild onClick={closeSheet}>
                      <a href="https://app.tellcasey.com/auth/login" target="_blank" rel="noopener noreferrer">Login</a>
                    </Button>
                    <Button variant="outline" asChild onClick={closeSheet}>
                      <Link to="/early-access">Book a Demo</Link>
                    </Button>
                    <Button asChild onClick={closeSheet}>
                      <Link to="/early-access">Get Started</Link>
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
